import { fetchQuery, graphql } from 'react-relay';
import { convertToProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import {
	startCaptureGraphQlErrors,
	stopCaptureGraphQlErrors,
} from '@jira/platform__graphql-relay-errors/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import fetchIssueDescriptionSmartlinkDataQuery, {
	type fetchIssueDescriptionSmartlinkDataQuery as fetchIssueDescriptionSmartlinkDataQueryType,
	type fetchIssueDescriptionSmartlinkDataQuery$data as fetchIssueDescriptionSmartlinkDataQueryResponse,
} from './__generated__/fetchIssueDescriptionSmartlinkDataQuery.graphql.ts';
import { fetchORSDataSSR, canCallORS, type PrefetchedSmartlinkData } from './fetchORSDataSSR.tsx';

type IssueKey = string;
type CloudId = string;

const SMART_LINKS_FIRST = 4;
const FETCH_ISSUE_DESCRIPTION_SMARTLINK_DATA = 'fetch-issue-description-smartlink-data';

export const FETCH_ISSUE_DESCRIPTION_SMARTLINK_DATA_OPERATION_NAME: string =
	fetchIssueDescriptionSmartlinkDataQuery.params.name;

function extractSmartLinkUrls(
	data: fetchIssueDescriptionSmartlinkDataQueryResponse | null | undefined,
): string[] {
	const edges = fg('jpx-1321_use_faster_fields_in_fetch_smart_links_2')
		? (data?.jira?.issueByKey?.description?.adfValue?.smartLinks?.edges ?? [])
		: (data?.jira?.issueByKey?.descriptionField?.richText?.adfValue?.smartLinks?.edges ?? []);
	const urls: string[] = [];
	for (const edge of edges) {
		const url = edge?.node?.url;
		if (url) {
			urls.push(url);
		}
	}
	return urls;
}

export const fetchIssueDescriptionSmartlinkData = async (
	issueKey: IssueKey,
	cloudId: CloudId,
): Promise<PrefetchedSmartlinkData[] | null> => {
	if (!canCallORS()) {
		return null;
	}

	const captureId = startCaptureGraphQlErrors();

	const environment = getRelayEnvironment();

	const graphqlData = await fetchQuery<fetchIssueDescriptionSmartlinkDataQueryType>(
		environment,
		// eslint-disable-next-line jira/relay/no-query
		graphql`
			query fetchIssueDescriptionSmartlinkDataQuery(
				$issueKey: String!
				$cloudId: ID!
				$first: Int!
				$after: String!
				$useFasterFields: Boolean!
			) {
				jira {
					issueByKey(key: $issueKey, cloudId: $cloudId) {
						descriptionField @skip(if: $useFasterFields) {
							... on JiraRichTextField {
								richText {
									adfValue {
										smartLinks(first: $first, after: $after) {
											edges {
												node {
													url
												}
											}
										}
									}
								}
							}
						}
						projectField @skip(if: $useFasterFields) {
							project {
								projectType
							}
						}
						description @include(if: $useFasterFields) {
							adfValue {
								smartLinks(first: $first, after: $after) {
									edges {
										node {
											url
										}
									}
								}
							}
						}
						project @include(if: $useFasterFields) {
							projectType
						}
					}
				}
			}
		`,
		{
			issueKey,
			cloudId,
			first: SMART_LINKS_FIRST,
			after: '',
			useFasterFields: fg('jpx-1321_use_faster_fields_in_fetch_smart_links_2'),
		},
	)
		.toPromise()
		.catch((error) => {
			throw error;
		});

	stopCaptureGraphQlErrors(captureId);

	const urls = extractSmartLinkUrls(graphqlData);
	const projectTypeData = fg('jpx-1321_use_faster_fields_in_fetch_smart_links_2')
		? graphqlData?.jira?.issueByKey?.project?.projectType
		: graphqlData?.jira?.issueByKey?.projectField?.project?.projectType;
	const projectType = projectTypeData ? convertToProjectType(projectTypeData) : undefined;

	if (urls.length === 0) {
		return null;
	}

	return fetchORSDataSSR(urls, FETCH_ISSUE_DESCRIPTION_SMARTLINK_DATA, projectType, cloudId);
};

export default fetchIssueDescriptionSmartlinkData;
